import { useAppDispatch, useAppSelector } from '@app/store';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { ContentRepurposingStatus, GeneratedContentEntry } from '../domain';
import { selectGeneratedContentById } from '../selector';

type UseContentCardContentReturn = {
  /** Ref to attach to the card container for visibility detection */
  ref: (node?: Element | null) => void;
  /** The fetched content options, or null if not loaded */
  content: { options: string[] } | null;
  /** Whether content is currently being fetched or generating */
  isLoading: boolean;
  /** Whether AI is still generating content (status is INITIAL or IN_PROGRESS) */
  isGenerating: boolean;
  /** Whether content fetch failed */
  isError: boolean;
  /** Error message if fetch failed */
  error: string | undefined;
  /** Function to retry fetching content */
  retry: () => void;
  /** The full entry state from cache */
  entry: GeneratedContentEntry;
};

/**
 * Hook to manage content fetching for a generated content card.
 *
 * Features:
 * - Lazy loading: Only fetches when card becomes visible
 * - Status-aware: Only fetches when content is ready on server (status === 'FINISHED')
 * - Single fetch: Uses triggerOnce to prevent refetching
 * - Cache-aware: Checks Redux cache before triggering fetch
 * - Retry support: Provides retry function for failed fetches
 *
 * @param contentId - The ID of the content to fetch
 * @param generationStatus - The API generation status from the history item (source of truth)
 * @returns Object with ref, content, loading/error states, and retry function
 */
export function useContentCardContent(
  contentId: string,
  generationStatus: ContentRepurposingStatus,
): UseContentCardContentReturn {
  const dispatch = useAppDispatch();

  // Get current entry from cache
  const entry = useAppSelector(state =>
    selectGeneratedContentById(state, contentId),
  );

  // Intersection observer - trigger once when card becomes visible
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1, // Trigger when 10% visible
  });

  // Content is ready to fetch only when server has finished generating
  const isContentReady = generationStatus === 'FINISHED';

  // Trigger fetch when:
  // 1. Card becomes visible (inView)
  // 2. Content not yet fetched (fetchStatus === 'idle')
  // 3. Content is ready on server (generationStatus === 'FINISHED')
  useEffect(() => {
    if (inView && entry.fetchStatus === 'idle' && isContentReady) {
      dispatch({
        type: '[content-composer] fetch content by id',
        payload: { contentId },
      });
    }
  }, [inView, entry.fetchStatus, contentId, dispatch, isContentReady]);

  // Retry function - clears failed state and re-triggers fetch
  // Note: Retry should only be used when content is ready (UI should disable retry otherwise)
  const retry = () => {
    // Dispatch fetch event - the saga will handle it
    // Failed entries will be overwritten by the 'started' reducer
    dispatch({
      type: '[content-composer] fetch content by id',
      payload: { contentId },
    });
  };

  // Determine loading state:
  // - If content is being fetched from server: fetchStatus === 'loading'
  // - If content is not ready yet (still generating): generationStatus is INITIAL or IN_PROGRESS
  const isGenerating =
    generationStatus === 'INITIAL' || generationStatus === 'IN_PROGRESS';
  const isFetching = entry.fetchStatus === 'loading';

  return {
    ref,
    content: entry.content,
    isLoading: isFetching || isGenerating,
    isGenerating,
    isError: entry.fetchStatus === 'failed' && !isGenerating, // Don't show error if still generating
    error: entry.error,
    retry,
    entry,
  };
}
